/* eslint-disable array-callback-return */
import React from 'react';

import {
  Button,
  Box,
  Typography,
  Modal,
  Grid,
  Stack,
  IconButton,
} from '@mui/material';
import produce from 'immer';

import { useGetModifiersQuery } from '../../../../../common/apis/FlorenceApi';
import { ReactComponent as CloseIcon } from '../../../../../common/assets/icons/cancel_icon_blue.svg';
import { SelectableCode } from '../../../../../common/types/Codes';
import { Modifier } from '../../../../types/Modifier';

import ModifierPopupCell from './ModifierPopupCell';

import './ModifierPopup.scss';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  borderRadius: '15px',

  textAlign: 'center',
  width: '100%',
  maxWidth: '1360px',
  maxHeight: 'calc(100% - 128px)',
  overflowY: 'scroll',
};

type Props = {
  open: boolean;
  code: SelectableCode;
  onClose: () => void;
  onConfirm: (modifierCodes: string[]) => void;
};

const ModifierPopupMenu: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  code,
}) => {
  const onCloseEvent = (
    event: never,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose();
  };

  const { data } = useGetModifiersQuery();

  const [modifiers, setModifiers] = React.useState<Modifier[]>([]);

  const selectedModifiersLength = modifiers.filter((m) => m.selected).length;

  React.useEffect(() => {
    if (data) setModifiers(data);
  }, [data]);

  React.useEffect(() => {
    setModifiers((prev) => {
      const newModifiers = [...prev];
      if (code.modifiers) {
        for (const mod of code.modifiers) {
          const index = newModifiers.findIndex(
            (m) => m.cptModifier === mod.toString()
          );
          if (index !== -1) {
            newModifiers[index] = { ...newModifiers[index], selected: true };
          }
        }
      }

      return newModifiers;
    });
  }, [code]);

  const handleSelection = (modifier: Modifier, checked: boolean) => {
    const updated = produce(modifiers, (draft) => {
      const index = draft.findIndex(
        (m) => m.cptModifier === modifier.cptModifier
      );
      if (index !== -1) draft[index].selected = checked;
    });
    setModifiers(updated);
  };

  const didTapConfirm = () => {
    const modifierCodes = modifiers
      .filter((m) => m.selected)
      .map((m) => m.cptModifier);
    onConfirm(modifierCodes);
  };

  const didTapClear = () => {
    const updated = produce(modifiers, (draft) => {
      for (const mod of draft) {
        mod.selected = false;
      }
    });
    setModifiers(updated);
  };

  return (
    <Modal open={open} onClose={onCloseEvent}>
      <Box sx={modalStyle} className='modifier-popup-container'>
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              textAlign='left'
              fontFamily='SFProDisplay-Semibold'
              variant='h5'
            >
              Select modifier
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Grid
            container
            direction='row'
            justifyContent='space-between'
            marginTop={'30px'}
          >
            {[0, 1, 2].map((section) => (
              <Grid key={section} item>
                {modifiers.map((modifier, index) => {
                  if (index % 3 === section) {
                    return (
                      <ModifierPopupCell
                        key={index}
                        handleSelection={handleSelection}
                        modifier={modifier}
                      />
                    );
                  }
                })}
              </Grid>
            ))}
          </Grid>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              width: '100%',
              gap: '16px',
            }}
          >
            <Button
              disabled={selectedModifiersLength === 0}
              onClick={didTapClear}
              variant='text'
            >
              <Typography
                color={
                  selectedModifiersLength === 0 ? 'text.secondary' : 'error'
                }
              >
                Clear all
              </Typography>
            </Button>
            <Button
              onClick={didTapConfirm}
              disableElevation
              color='primary'
              variant='contained'
              sx={{
                borderRadius: '50px',
                width: '160px',
                height: '50px',
              }}
            >
              <Typography color='white'>Apply</Typography>
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default ModifierPopupMenu;
